import { Account, Gender, UserProfile } from './UserProfile';
import { Column } from '../Dynamic';
import { BASE_API } from '../../config/applicationConfig';
import { getDate, getGender } from '../../util/RenderUtils';
import { authenticatedFetch } from '../../api/apiClient';

export const userProfileColumns: Column<UserProfile>[] = [
  {
    id: 'image',
    label: 'Image',
    minWidth: 150,
    inputType: 'image',
    group: 'Bild',
    gridWidth: 6,
  },
  {
    id: 'firstname',
    label: 'Vorname',
    inputType: 'text',
    required: true,
    validate: [value => (!value ? 'Vorname ist erforderlich' : null)],
    group: 'Persönliche Informationen',
    gridWidth: 6,
  },
  {
    id: 'name',
    label: 'Nachname',
    inputType: 'text',
    required: true,
    validate: [value => (!value ? 'Nachname ist erforderlich' : null)],
    group: 'Persönliche Informationen',
    gridWidth: 6,
  },
  {
    id: 'email',
    label: 'E-Mail',
    inputType: 'text',
    required: true,
    disabled: true,
    validate: [
      value => (!value ? 'E-Mail ist erforderlich' : null),
      value => (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Ungültige E-Mail-Adresse' : null),
    ],
    group: 'Persönliche Informationen',
    gridWidth: 6,
  },
  {
    id: 'phone',
    label: 'Telefon',
    inputType: 'text',
    required: false,
    validate: [
      value => (value && !/^[0-9+\s()-]{5,20}$/.test(value) ? 'Ungültige Telefonnummer' : null),
    ],
    group: 'Persönliche Informationen',
    gridWidth: 6,
  },
  {
    id: 'gender',
    label: 'Geschlecht',
    inputType: 'select',
    required: false,
    options: [
      { label: 'Männlich', value: Gender.MALE },
      { label: 'Weiblich', value: Gender.FEMALE },
      { label: 'Andere', value: Gender.OTHER },
      { label: 'Unbekannt', value: Gender.UNKNOWN },
    ],
    group: 'Persönliche Informationen',
    gridWidth: 6,
    format: value => getGender(value as Gender),
  },
  {
    id: 'birthdate',
    label: 'Geburtsdatum',
    inputType: 'date',
    required: false,
    group: 'Persönliche Informationen',
    gridWidth: 6,
  },
  {
    id: 'birthplace',
    label: 'Geburtsort',
    inputType: 'text',
    required: false,
    group: 'Persönliche Informationen',
    gridWidth: 12,
  },
  {
    id: 'street',
    label: 'Straße',
    inputType: 'text',
    required: false,
    group: 'Adressinformationen',
    gridWidth: 12,
  },
  {
    id: 'streetNumber',
    label: 'Hausnummer',
    inputType: 'text',
    required: false,
    group: 'Adressinformationen',
    gridWidth: 6,
  },
  {
    id: 'addressAdd',
    label: 'Zusatz',
    inputType: 'text',
    required: false,
    group: 'Adressinformationen',
    gridWidth: 6,
  },
  {
    id: 'city',
    label: 'Stadt',
    inputType: 'text',
    required: false,
    group: 'Adressinformationen',
    gridWidth: 6,
  },
  {
    id: 'bic',
    label: 'PLZ',
    inputType: 'text',
    required: false,
    group: 'Adressinformationen',
    gridWidth: 6,
  },
  {
    id: 'keycloakId',
    label: 'Keycloak ID',
    minWidth: 150,
    inputType: 'text',
    disabled: true,
    group: 'Account',
    gridWidth: 12,
  },
];

/**
 * Erstellt ein neues Benutzerprofil
 * @param userProfile Das zu erstellende Benutzerprofil
 * @returns Das erstellte Benutzerprofil
 */
export const createUserProfile = async (userProfile: UserProfile): Promise<UserProfile> => {
  const response = await authenticatedFetch(BASE_API + '/user-profile', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userProfile),
  });

  if (!response.ok) {
    throw new Error(`Fehler beim Erstellen des Benutzerprofils: ${response.statusText}`);
  }

  return response.json();
};
/**
 * Aktualisiert ein bestehendes Benutzerprofil
 * @param userProfile Das zu aktualisierende Benutzerprofil
 * @returns Das aktualisierte Benutzerprofil
 */
export const updateUserProfile = async (userProfile: UserProfile): Promise<UserProfile> => {
  const response = await authenticatedFetch(BASE_API + '/user-profile', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userProfile),
  });

  if (!response.ok) {
    throw new Error(`Fehler beim Aktualisieren des Benutzerprofils: ${response.statusText}`);
  }

  return response.json();
};
/**
 * Findet oder erstellt ein Benutzerprofil basierend auf der E-Mail-Adresse
 * @param account Das Account-Objekt mit der E-Mail-Adresse
 * @returns Das gefundene oder erstellte Benutzerprofil
 */
export const findOrCreateUserProfile = async (account: Account): Promise<UserProfile> => {
  const response = await authenticatedFetch(`${BASE_API}/user-profile/by-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(account),
  });

  if (!response.ok) {
    throw new Error(`Fehler beim Suchen/Erstellen des Benutzerprofils: ${response.statusText}`);
  }

  return response.json();
};
/**
 * Löscht ein Benutzerprofil
 * @param id Die ID des zu löschenden Benutzerprofils
 * @returns true, wenn das Löschen erfolgreich war
 */
export const deleteUserProfile = async (id: number): Promise<boolean> => {
  const response = await authenticatedFetch(`${BASE_API}/user-profile?id=${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Fehler beim Löschen des Benutzerprofils: ${response.statusText}`);
  }

  return true;
};
